import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Plus, Trash2 } from "lucide-react";

// Beast V1 Checkpoint 4 — Job detail drawer (caveman-simple).
//
// Shows Customer / Title / Description / Received / Due / Commercial
// Mode snapshot (read-only) / Work Status / Billing Status /
// Deliverables (description + hours + no_charge + delete) / Notes.
// Deliberately NOT a project management screen — no comments, activity
// feed, timelines or assignees.
const WORK_STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting_on_client", label: "Waiting on Client" },
  { value: "complete", label: "Complete" },
  { value: "cancelled", label: "Cancelled" },
];

const BILLING_STATUSES = [
  { value: "not_ready", label: "Not Ready" },
  { value: "ready_to_bill", label: "Ready to Bill" },
  { value: "invoice_sent", label: "Invoice Sent" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "no_charge", label: "No Charge" },
];

const commercialLabel = (mode) => {
  if (mode === "managed") return "MANAGED";
  if (mode === "retainer") return "RETAINER";
  return "STANDARD";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso.slice(0, 10);
  }
};

export const JobDetailDrawer = ({ jobId, onClose, onChanged }) => {
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", due_at: "", notes: "" });
  const [newDeliv, setNewDeliv] = useState({ description: "", hours: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/jobs/${jobId}`);
      setJob(data);
      setDraft({
        title: data.title || "",
        description: data.description || "",
        due_at: data.due_at ? data.due_at.slice(0, 10) : "",
        notes: data.notes || "",
      });
      // Load the linked customer once so we can show its display_name.
      if (data.customer_id) {
        try {
          const cr = await api.get(`/admin/customers/${data.customer_id}`);
          setCustomer(cr.data);
        } catch {
          setCustomer(null);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load job");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const patchJob = async (partial) => {
    if (!job) return;
    setBusy(true);
    try {
      const payload = {};
      if (partial.title !== undefined) payload.title = partial.title;
      if (partial.description !== undefined) payload.description = partial.description;
      if (partial.due_at !== undefined) payload.due_at = partial.due_at;
      if (partial.notes !== undefined) payload.notes = partial.notes;
      if (partial.work_status !== undefined) payload.work_status = partial.work_status;
      if (partial.billing_status !== undefined) payload.billing_status = partial.billing_status;
      const { data } = await api.patch(`/admin/jobs/${job.id}`, payload);
      setJob(data);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const saveTextEdits = async () => {
    const dueIso = draft.due_at ? `${draft.due_at}T00:00:00Z` : "";
    await patchJob({
      title: draft.title,
      description: draft.description,
      due_at: dueIso,
      notes: draft.notes,
    });
    toast.success("Job saved");
  };

  const addDeliverable = async () => {
    if (!newDeliv.description.trim()) {
      toast.error("Deliverable description required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/jobs/${job.id}/deliverables`, {
        description: newDeliv.description.trim(),
        hours: Number(newDeliv.hours) || 0,
        no_charge: false,
      });
      setNewDeliv({ description: "", hours: 0 });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add deliverable");
    } finally {
      setBusy(false);
    }
  };

  const patchDeliverable = async (id, partial) => {
    setBusy(true);
    try {
      await api.patch(`/admin/jobs/${job.id}/deliverables/${id}`, partial);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update deliverable");
    } finally {
      setBusy(false);
    }
  };

  const deleteDeliverable = async (id) => {
    if (!confirm("Delete this deliverable?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/jobs/${job.id}/deliverables/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete deliverable");
    } finally {
      setBusy(false);
    }
  };

  const totalHours = useMemo(() => {
    if (!job) return 0;
    return (job.deliverables || []).reduce((s, d) => s + (Number(d.hours) || 0), 0);
  }, [job]);

  return (
    <div
      data-testid="job-detail-drawer"
      className="fixed inset-0 z-[9998] bg-black/70 flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl h-full bg-[#0F0F0F] border-l border-[#262626] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-[#1F1F1F] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="ur-eyebrow">Job</div>
            <div className="text-sm text-[#A3A3A3] mt-0.5">
              {loading ? "…" : customer?.display_name || "Unknown customer"}
            </div>
          </div>
          <button
            data-testid="job-detail-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center hover:border-[#FF6600]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading || !job ? (
          <div className="p-6 text-sm text-[#A3A3A3] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Commercial Mode snapshot — read-only */}
            <div className="flex items-center gap-3 flex-wrap">
              <span
                data-testid="job-commercial-mode"
                className="inline-flex items-center rounded-full bg-[#FF6600]/12 border border-[#FF6600]/40 text-[#FF6600] text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1"
                title="Commercial Mode was snapshotted from the Customer when this Job was created. It does not change if the Customer's mode later changes."
              >
                {commercialLabel(job.commercial_mode_snapshot)}
              </span>
              <span className="text-[10px] text-[#5A5A5A] uppercase tracking-widest">
                snapshot at job start
              </span>
            </div>

            {/* Basic fields */}
            <label className="text-xs text-[#A3A3A3] block">
              Title
              <input
                data-testid="job-title-input"
                className="ur-input mt-1 w-full"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>

            <label className="text-xs text-[#A3A3A3] block">
              Description
              <textarea
                data-testid="job-description-input"
                rows={3}
                className="ur-input mt-1 w-full"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-xs text-[#A3A3A3]">
                Received
                <div data-testid="job-received-at" className="mt-1 text-sm text-white">
                  {formatDate(job.received_at)}
                </div>
              </div>
              <label className="text-xs text-[#A3A3A3] block">
                Due Date
                <input
                  data-testid="job-due-at-input"
                  type="date"
                  className="ur-input mt-1 w-full"
                  value={draft.due_at}
                  onChange={(e) => setDraft({ ...draft, due_at: e.target.value })}
                />
              </label>
            </div>

            {/* Origin */}
            {job.origin_ref && (
              <div className="text-xs text-[#A3A3A3]">
                Origin
                <div data-testid="job-origin-ref" className="mt-1 text-sm text-white">
                  {job.origin_ref.ref_type}
                  {job.origin_ref.ref_id ? (
                    <span className="ml-2 font-mono text-[11px] text-[#A3A3A3]">
                      {job.origin_ref.ref_id}
                    </span>
                  ) : null}
                </div>
              </div>
            )}

            {/* Status controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-xs text-[#A3A3A3] block">
                Work Status
                <select
                  data-testid="job-work-status"
                  className="ur-input mt-1 w-full"
                  value={job.work_status}
                  onChange={(e) => patchJob({ work_status: e.target.value })}
                  disabled={busy}
                >
                  {WORK_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-[#A3A3A3] block">
                Billing Status
                <select
                  data-testid="job-billing-status"
                  className="ur-input mt-1 w-full"
                  value={job.billing_status}
                  onChange={(e) => patchJob({ billing_status: e.target.value })}
                  disabled={busy}
                >
                  {BILLING_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Deliverables */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="ur-eyebrow">Deliverables</div>
                <div
                  data-testid="job-total-hours"
                  className="text-xs text-[#A3A3A3]"
                >
                  Total: <span className="text-white font-semibold">{totalHours.toFixed(2)}h</span>
                </div>
              </div>

              <div className="space-y-2" data-testid="job-deliverables-list">
                {(job.deliverables || []).map((d) => (
                  <div
                    key={d.id}
                    data-testid={`deliverable-row-${d.id}`}
                    className="flex items-center gap-2 bg-[#141414] border border-[#1F1F1F] rounded-lg p-2"
                  >
                    <input
                      data-testid={`deliverable-description-${d.id}`}
                      className="ur-input flex-1 text-sm"
                      defaultValue={d.description}
                      onBlur={(e) => {
                        if (e.target.value !== d.description) {
                          patchDeliverable(d.id, { description: e.target.value });
                        }
                      }}
                    />
                    <input
                      data-testid={`deliverable-hours-${d.id}`}
                      type="number"
                      step="0.25"
                      min="0"
                      className="ur-input w-24 text-sm"
                      defaultValue={d.hours}
                      onBlur={(e) => {
                        const v = Number(e.target.value) || 0;
                        if (v !== d.hours) patchDeliverable(d.id, { hours: v });
                      }}
                    />
                    <label
                      className="text-xs text-[#A3A3A3] inline-flex items-center gap-1.5 whitespace-nowrap"
                      title="No Charge — track hours but exclude from billing."
                    >
                      <input
                        data-testid={`deliverable-no-charge-${d.id}`}
                        type="checkbox"
                        checked={!!d.no_charge}
                        onChange={(e) => patchDeliverable(d.id, { no_charge: e.target.checked })}
                      />
                      No Charge
                    </label>
                    <button
                      data-testid={`deliverable-delete-${d.id}`}
                      onClick={() => deleteDeliverable(d.id)}
                      className="w-8 h-8 rounded-full border border-[#262626] grid place-items-center hover:border-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add deliverable */}
              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid="new-deliverable-description"
                  className="ur-input flex-1 text-sm"
                  placeholder="Deliverable description"
                  value={newDeliv.description}
                  onChange={(e) => setNewDeliv({ ...newDeliv, description: e.target.value })}
                />
                <input
                  data-testid="new-deliverable-hours"
                  type="number"
                  step="0.25"
                  min="0"
                  className="ur-input w-24 text-sm"
                  value={newDeliv.hours}
                  onChange={(e) => setNewDeliv({ ...newDeliv, hours: e.target.value })}
                />
                <button
                  data-testid="new-deliverable-add"
                  onClick={addDeliverable}
                  disabled={busy}
                  className="ur-btn-primary text-sm py-2 px-3"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notes */}
            <label className="text-xs text-[#A3A3A3] block">
              Internal Notes
              <textarea
                data-testid="job-notes-input"
                rows={2}
                className="ur-input mt-1 w-full"
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </label>

            <div className="flex justify-end">
              <button
                data-testid="job-save-edits"
                onClick={saveTextEdits}
                disabled={busy}
                className="ur-btn-primary text-sm"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
